import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Circle } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../../lib/api";
import { useSite, useSeo, useFavorites } from "../../lib/site";
import { SiteLayout } from "../../components/site/SiteLayout";
import { SiteCard, publicImg, priceLabel } from "../../components/site/SiteCard";
import { LeadForm } from "../../components/site/LeadForm";
import { ArrowLeft, BedDouble, Bath, Ruler, Zap, CalendarDays, Trees, Car, Check, X, ChevronLeft, ChevronRight, Heart, Video, Rotate3d, MapPin } from "lucide-react";

function Gallery({ media, title }) {
  const [i, setI] = useState(null);
  useEffect(() => {
    if (i == null) return undefined;
    const on = (e) => {
      if (e.key === "Escape") setI(null);
      if (e.key === "ArrowRight") setI((x) => (x + 1) % media.length);
      if (e.key === "ArrowLeft") setI((x) => (x - 1 + media.length) % media.length);
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [i, media.length]);
  if (!media.length) return <div className="aspect-[16/9] rounded-3xl bg-white/5" />;
  const move = (d) => setI((i + d + media.length) % media.length);
  return (
    <>
      <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[320px] sm:h-[480px] rounded-3xl overflow-hidden" data-testid="pp-gallery">
        {media.slice(0, 5).map((m, idx) => (
          <button key={m.id} onClick={() => setI(idx)} data-testid={`pp-photo-${idx}`} className={`relative overflow-hidden group ${idx === 0 ? `col-span-4 ${media.length > 1 ? "sm:col-span-2" : ""} row-span-2` : "hidden sm:block"}`}>
            <img src={publicImg(m.url)} alt={m.caption || title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            {idx === 4 && media.length > 5 && <span className="absolute inset-0 bg-black/55 flex items-center justify-center text-white font-semibold">+{media.length - 5}</span>}
          </button>
        ))}
      </div>
      {i != null && (
        <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center" onClick={() => setI(null)} data-testid="pp-lightbox">
          <button className="absolute top-5 right-5 text-white" data-testid="pp-lightbox-close"><X size={28} /></button>
          <button className="absolute left-3 text-white p-2" onClick={(e) => { e.stopPropagation(); move(-1); }}><ChevronLeft size={38} /></button>
          <figure className="px-14" onClick={(e) => e.stopPropagation()}>
            <img src={publicImg(media[i].url)} alt={media[i].caption} className="max-h-[82vh] max-w-full object-contain mx-auto" />
            <figcaption className="text-center text-sm text-slate-300 mt-3">{media[i].caption || media[i].category} · {i + 1}/{media.length}</figcaption>
          </figure>
          <button className="absolute right-3 text-white p-2" onClick={(e) => { e.stopPropagation(); move(1); }}><ChevronRight size={38} /></button>
        </div>
      )}
    </>
  );
}

const Spec = ({ icon: I, label, value }) => value == null || value === "" ? null : (
  <div className="flex items-center gap-3 bg-white/5 rounded-2xl px-4 py-3">
    <I size={18} className="text-[#C9A84C]" /><div><p className="text-white font-semibold text-sm">{value}</p><p className="text-[11px] text-slate-400">{label}</p></div>
  </div>
);

export default function SiteProperty() {
  const { id } = useParams();
  const { t, money, types, lang } = useSite();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const { fav, toggle } = useFavorites();
  const tx = (k) => (p && ((lang === "en" && p[`${k}_en`]) || p[k])) || "";
  useSeo(p ? `${tx("title")} | Grupo Almar Investimentos` : "Grupo Almar Investimentos", tx("description").slice(0, 155));

  useEffect(() => { window.scrollTo(0, 0); setP(null); api.get(`/public/properties/${id}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [id]);

  if (err) return <SiteLayout><div className="pt-40 pb-32 text-center text-slate-400" data-testid="pp-not-found">404 · <Link to="/propriedades" className="text-[#E0C574]">{t("back")}</Link></div></SiteLayout>;
  if (!p) return <SiteLayout><div className="pt-32 max-w-7xl mx-auto px-5"><div className="h-[480px] rounded-3xl bg-white/5 animate-pulse" /></div></SiteLayout>;

  const feats = ["garage", "elevator", "balcony", "terrace", "garden", "pool", "storage_room", "furnished"].filter((k) => p[k]);
  return (
    <SiteLayout>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-20">
        <Link to="/propriedades" className="text-sm text-slate-400 hover:text-[#E0C574] inline-flex items-center gap-1 mb-5" data-testid="pp-back"><ArrowLeft size={14} /> {t("back")}</Link>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C]">{types[p.property_type]} · {t(`pu_${p.purpose}`)} · {t("ref")} {p.reference}</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-2" data-testid="pp-title">{tx("title")}</h1>
            <p className="text-slate-400 mt-2 flex items-center gap-1"><MapPin size={14} />{[p.address, p.parish, p.municipality, p.district].filter(Boolean).join(", ")}</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-display text-3xl text-[#E0C574]" data-testid="pp-price">{priceLabel(p, t, money)}</span>
            {p.sale_price && p.rent_price ? <span className="text-sm text-slate-400">· {money(p.rent_price)}{t("per_month")}</span> : null}
            <button onClick={() => toggle(p.id)} data-testid="pp-fav" className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center"><Heart size={18} className={fav.includes(p.id) ? "fill-[#C9A84C] text-[#C9A84C]" : ""} /></button>
          </div>
        </div>
        <Gallery media={p.media} title={tx("title")} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-10">
          <div className="lg:col-span-2 space-y-10">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="pp-specs">
              <Spec icon={BedDouble} label={t("bedrooms")} value={p.bedrooms} />
              <Spec icon={Bath} label={t("bathrooms")} value={p.bathrooms} />
              <Spec icon={Ruler} label={t("area")} value={p.area_useful ? `${p.area_useful} m²` : null} />
              <Spec icon={Trees} label={t("land")} value={p.area_land ? `${p.area_land} m²` : null} />
              <Spec icon={Zap} label={t("energy")} value={p.energy_class} />
              <Spec icon={CalendarDays} label={t("year")} value={p.year_built} />
              <Spec icon={Car} label={t("parking")} value={p.parking_spaces || null} />
            </div>
            {tx("description") && <div><h2 className="font-display text-2xl text-white mb-3">{t("description")}</h2><p className="text-slate-300 leading-relaxed whitespace-pre-line" data-testid="pp-description">{tx("description")}</p></div>}
            {(feats.length > 0 || p.custom_features?.length > 0) && (
              <div><h2 className="font-display text-2xl text-white mb-3">{t("features")}</h2>
                <div className="flex flex-wrap gap-2">{feats.map((k) => <span key={k} className="inline-flex items-center gap-1 text-sm bg-[#C9A84C]/10 text-[#E0C574] rounded-full px-3 py-1.5"><Check size={13} />{t(k)}</span>)}
                  {(p.custom_features || []).map((c) => <span key={c} className="inline-flex items-center gap-1 text-sm bg-white/5 text-slate-200 rounded-full px-3 py-1.5"><Check size={13} />{c}</span>)}</div>
              </div>
            )}
            {(p.video_url || p.virtual_tour_url) && (
              <div className="flex flex-wrap gap-3">
                {p.virtual_tour_url && <a href={p.virtual_tour_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#C9A84C]/50 text-[#E0C574] rounded-full px-5 py-2" data-testid="pp-tour"><Rotate3d size={16} /> {t("virtual_tour")}</a>}
                {p.video_url && <a href={p.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#C9A84C]/50 text-[#E0C574] rounded-full px-5 py-2" data-testid="pp-video"><Video size={16} /> {t("video")}</a>}
              </div>
            )}
            {p.map && (
              <div><h2 className="font-display text-2xl text-white mb-3">{t("location_title")}</h2>
                <div className="h-80 rounded-3xl overflow-hidden relative z-0" data-testid="pp-map">
                  <MapContainer center={[p.map.lat, p.map.lng]} zoom={p.map.exact ? 15 : 13} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
                    <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    {p.map.exact ? <CircleMarker center={[p.map.lat, p.map.lng]} radius={10} pathOptions={{ color: "#C9A84C", fillOpacity: 0.7 }} />
                      : <Circle center={[p.map.lat, p.map.lng]} radius={900} pathOptions={{ color: "#C9A84C", fillOpacity: 0.15 }} />}
                  </MapContainer>
                </div>
                {!p.map.exact && <p className="text-xs text-slate-500 mt-2">{t("approx")}</p>}
              </div>
            )}
          </div>
          <aside className="lg:sticky lg:top-28 h-fit space-y-4">
            {p.consultant && (
              <div className="bg-white/5 rounded-3xl p-5 flex items-center gap-4" data-testid="pp-consultant">
                <img src={p.consultant.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(p.consultant.name)}`} alt="" className="w-14 h-14 rounded-full object-cover ring-2 ring-[#C9A84C]/40" />
                <div className="min-w-0"><p className="text-[11px] uppercase tracking-wider text-slate-400">{t("your_consultant")}</p><p className="text-white font-semibold">{p.consultant.name}</p><p className="text-xs text-slate-400 truncate">{p.consultant.phone}</p></div>
              </div>
            )}
            <div className="bg-[#122436] border border-white/10 rounded-3xl p-6">
              <h3 className="font-display text-xl text-white mb-4">{t("form_title")}</h3>
              <LeadForm kind="info" allowKinds={["info", "visita"]} propertyId={p.id} testPrefix="pp-lead" />
            </div>
          </aside>
        </div>
        {p.similar?.length > 0 && (
          <div className="mt-20"><h2 className="font-display text-2xl sm:text-3xl text-white mb-6">{t("similar")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{p.similar.map((s, i) => <SiteCard key={s.id} p={s} i={i} fav={fav.includes(s.id)} onFav={toggle} />)}</div>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}
